'use client';

import { c as _c } from "react/compiler-runtime";
import { StepBar } from '@/components/ui/step-bar';
import useRegisterStore from '@/stores/register';
import RegisterWelcomeStep from './steps/RegisterWelcomeStep';
import OtpVerificationStep from './steps/OtpVerificationStep';
import RegisterFormStep from './steps/RegisterFormStep';
import RegisterPersonalDataStep from './steps/RegisterPersonalDataStep';
import RegisterOnboardingBankSelectionStep from './steps/RegisterOnboardingBankSelectionStep';
import RegisterSuccessStep from './steps/RegisterSuccessStep';
import { Form } from '@/components/ui/form';
import useRegisterPageFactory from '@/components/RegisterPage/Factory';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import type { Bank } from '@/utils/bankImageMapper';
import { AnimatePresence, motion } from 'framer-motion';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { toast } from '@/components/toast';
import { showOtpSendToasts } from '@/lib/otp-toasts';
import { sendPreRegistrationWhatsAppVerification } from '@/app/(auth)/actions';
import { MASTER_PHONE_NUMBER, MASTER_PHONE_NUMBER_2 } from '@/lib/constants';
import { sanitizeBrazilianPhone } from '@/utils/formatPhoneNumber';
import useRudderStackAnalytics from '@/hooks/useRudderAnalytics';
import { useSession } from 'next-auth/react';
import useIsWebview from '@/hooks/use-is-webview';
import { useFirstTouchUTM } from '@/hooks/use-first-touch-utm';
import { usePageTransition } from '@/hooks/use-page-transition';
import { getPrefillEmail } from '@/lib/auth/prefill-email';
enum RegisterStep {
  WELCOME = 0,
  OTP_VERIFICATION = 1,
  REGISTRATION_FORM = 2,
  PERSONAL_DATA = 3,
  SUCCESS = 4,
  ONBOARDING_BANK_SELECTION = 5,
}
const STEPS_WITH_HEADER = [RegisterStep.OTP_VERIFICATION, RegisterStep.REGISTRATION_FORM, RegisterStep.PERSONAL_DATA, RegisterStep.ONBOARDING_BANK_SELECTION];
const ONBOARDING_STEPS = [RegisterStep.ONBOARDING_BANK_SELECTION];
interface RegisterPageComponentProps {
  banks: Bank[];
  brokerages?: Bank[];
}
const RegisterPageComponent = t0 => {
  const $ = _c(47);
  const {
    banks,
    brokerages: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const brokerages = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["currentStep", "totalSteps", "previousStep", "setSkippedOtp", "setCurrentStep"];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    currentStep,
    totalSteps,
    previousStep,
    setSkippedOtp,
    setCurrentStep
  } = useRegisterStore(t3);
  const {
    registerForm
  } = useRegisterPageFactory();
  const searchParams = useSearchParams();
  const hasProcessedPhoneParam = useRef(false);
  const hasTrackedPageView = useRef(false);
  const analytics = useRudderStackAnalytics();
  const {
    data: session
  } = useSession();
  const [isWebView] = useIsWebview();
  const firstTouchUTM = useFirstTouchUTM();
  usePageTransition();
  let t4;
  let t5;
  if ($[3] !== registerForm || $[4] !== searchParams || $[5] !== setCurrentStep || $[6] !== setSkippedOtp) {
    t4 = () => {
      const phoneParam = searchParams.get("phone");
      const emailParam = searchParams.get("email");
      const nameParam = searchParams.get("name");
      const providerParam = searchParams.get("provider");
      const providerUserIdParam = searchParams.get("providerUserId");
      const skipPasswordParam = searchParams.get("skipPassword");
      const prefillEmail = getPrefillEmail();
      if (!phoneParam && !emailParam && !providerParam && !prefillEmail || hasProcessedPhoneParam.current) {
        return;
      }
      hasProcessedPhoneParam.current = true;
      if (providerParam && providerUserIdParam && skipPasswordParam === "true") {
        if (emailParam) {
          registerForm.setValue("email", emailParam);
        }
        if (nameParam && !nameParam.includes("@")) {
          registerForm.setValue("name", nameParam);
        }
        setCurrentStep(RegisterStep.WELCOME);
        return;
      }
      const emailToPrefill = emailParam || prefillEmail;
      if (emailToPrefill && !phoneParam) {
        registerForm.setValue("email", emailToPrefill);
        setCurrentStep(RegisterStep.WELCOME);
        return;
      }
      if (!phoneParam) {
        return;
      }
      const sanitizedPhone = sanitizeBrazilianPhone(phoneParam);
      if (!sanitizedPhone) {
        toast({
          type: "error",
          description: "N\xFAmero de telefone inv\xE1lido."
        });
        setCurrentStep(RegisterStep.WELCOME);
        return;
      }
      const phoneWithoutCountryCode = sanitizedPhone.slice(2);
      const formattedPhone = `(${phoneWithoutCountryCode.slice(0, 2)}) ${phoneWithoutCountryCode.slice(2, 7)}-${phoneWithoutCountryCode.slice(7)}`;
      registerForm.setValue("phoneNumber", formattedPhone);
      const isMasterPhone = [MASTER_PHONE_NUMBER, MASTER_PHONE_NUMBER_2].some(masterPhoneNumber => Boolean(masterPhoneNumber) && sanitizedPhone === masterPhoneNumber);
      if (isMasterPhone) {
        setSkippedOtp(true);
        setCurrentStep(RegisterStep.REGISTRATION_FORM);
      } else {
        setSkippedOtp(false);
        setCurrentStep(RegisterStep.OTP_VERIFICATION);
        const formData = new FormData();
        formData.append("phoneNumber", sanitizedPhone);
        sendPreRegistrationWhatsAppVerification({
          status: "idle"
        }, formData).then(_temp).catch(_temp2);
      }
    };
    t5 = [searchParams, setSkippedOtp, setCurrentStep, registerForm];
    $[3] = registerForm;
    $[4] = searchParams;
    $[5] = setCurrentStep;
    $[6] = setSkippedOtp;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== currentStep || $[10] !== totalSteps) {
    const getProgressBarValues = () => {
      if (ONBOARDING_STEPS.includes(currentStep)) {
        const onboardingStepIndex = currentStep - RegisterStep.ONBOARDING_BANK_SELECTION;
        return {
          current: onboardingStepIndex + 1,
          total: ONBOARDING_STEPS.length
        };
      }
      return {
        current: currentStep,
        total: totalSteps
      };
    };
    t6 = getProgressBarValues();
    $[9] = currentStep;
    $[10] = totalSteps;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const progressBar = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      x: 20
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      initial: t7,
      animate: t8,
      exit: {
        opacity: 0,
        x: -20,
        transition: {
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        }
      }
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const stepVariants = t9;
  let t10;
  if ($[15] !== banks || $[16] !== brokerages || $[17] !== currentStep) {
    t10 = () => {
      switch (currentStep) {
        case RegisterStep.WELCOME:
          {
            return <RegisterWelcomeStep />;
          }
        case RegisterStep.OTP_VERIFICATION:
          {
            return <OtpVerificationStep />;
          }
        case RegisterStep.REGISTRATION_FORM:
          {
            return <RegisterFormStep />;
          }
        case RegisterStep.PERSONAL_DATA:
          {
            return <RegisterPersonalDataStep />;
          }
        case RegisterStep.SUCCESS:
          {
            return <RegisterSuccessStep />;
          }
        case RegisterStep.ONBOARDING_BANK_SELECTION:
          {
            return <RegisterOnboardingBankSelectionStep banks={banks} brokerages={brokerages} />;
          }
        default:
          {
            return <RegisterWelcomeStep />;
          }
      }
    };
    $[15] = banks;
    $[16] = brokerages;
    $[17] = currentStep;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const renderStep = t10;
  let t11;
  if ($[19] !== analytics || $[20] !== firstTouchUTM || $[21] !== isWebView || $[22] !== session?.user?.id) {
    t11 = () => {
      if (!analytics || hasTrackedPageView.current) {
        return;
      }
      hasTrackedPageView.current = true;
      analytics.page("", {
        page_name: "Register",
        platform: isWebView ? "app" : "web",
        is_logged_in: !!session?.user?.id,
        ...(firstTouchUTM || {})
      });
    };
    $[19] = analytics;
    $[20] = firstTouchUTM;
    $[21] = isWebView;
    $[22] = session?.user?.id;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = session?.user?.id;
  let t13;
  if ($[24] !== analytics || $[25] !== firstTouchUTM || $[26] !== isWebView || $[27] !== t12) {
    t13 = [analytics, isWebView, t12, firstTouchUTM];
    $[24] = analytics;
    $[25] = firstTouchUTM;
    $[26] = isWebView;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  useEffect(t11, t13);
  let t14;
  if ($[29] !== currentStep || $[30] !== previousStep) {
    t14 = STEPS_WITH_HEADER.includes(currentStep) && <div className="w-full relative hidden md:flex h-14 justify-center items-center pt-5"><Button variant="none" size="iconArea" onClick={previousStep} className="text-primary hover:text-white/80 transition-colors self-start absolute left-4 top-1/2 "><ArrowLeft className="!size-6" /></Button><Image src="/images/pierre-register.png" alt="Pierre logo" width={56} height={56} /></div>;
    $[29] = currentStep;
    $[30] = previousStep;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== currentStep || $[33] !== progressBar) {
    t15 = STEPS_WITH_HEADER.includes(currentStep) && <div className="fixed top-0 inset-x-0 z-50 md:top-20"><StepBar totalSteps={progressBar.total} currentStep={progressBar.current} variant="progress" height="sm" rounded="none" activeRoundedRight="full" activeClassName="bg-brand-bright-green" transparent={true} /></div>;
    $[32] = currentStep;
    $[33] = progressBar;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== renderStep) {
    t16 = <form onSubmit={_temp3}>{renderStep()}</form>;
    $[35] = renderStep;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== registerForm || $[38] !== t16) {
    t17 = <Form {...registerForm}>{t16}</Form>;
    $[37] = registerForm;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== currentStep || $[41] !== t17) {
    t18 = <AnimatePresence mode="wait"><motion.div key={currentStep} variants={stepVariants} initial="initial" animate="animate" exit="exit">{t17}</motion.div></AnimatePresence>;
    $[40] = currentStep;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t14 || $[44] !== t15 || $[45] !== t18) {
    t19 = <div className="w-full h-screen  md:overflow-hidden relative">{t14}{t15}{t18}</div>;
    $[43] = t14;
    $[44] = t15;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
};
export default RegisterPageComponent;
function _temp(result) {
  if (result.status === "success") {
    showOtpSendToasts(result, {
      channel: "whatsapp"
    });
  } else {
    toast({
      type: "error",
      description: "Erro ao enviar c\xF3digo. Tente novamente."
    });
  }
}
function _temp2() {
  toast({
    type: "error",
    description: "Erro ao enviar c\xF3digo. Tente novamente."
  });
}
function _temp3(e) {
  e.preventDefault();
  e.stopPropagation();
}